<script setup lang="ts">
import PageHeader from '@/components/PageHeader.vue'
import { computed, nextTick, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ProgressSpinner from 'primevue/progressspinner'
import WizardOverlayContainer from '@/components/WizardOverlayContainer.vue'
import WizardPrompt from '@/components/WizardPrompt.vue'
import { useUserFeedbackContext } from '@/components/useUserFeedbackContext'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import { betaOptIn } from '@/util/beta_opt_in'
import { buildFilters, EMPTY_SELECTION, selectionFromQuery, selectionToQuery } from './strategy_filters'
import { useStrategyTags } from './useStrategyTags'
import type { StrategyFilter, StrategyFilterSelection } from './strategy_types'

const OTHER_SUBJECT = 'Other'
const SUBJECT_PROMPT = '(subject)'
const BARRIER_PROMPT_FOR_OTHER = '(select barrier)'
const SUBMIT_DISABLED_REASON = 'Choose a barrier to find strategies'
const SUBMIT_DISABLED_REASON_ID = 'find-strategies-disabled-reason'

const PROMPTS: Record<string, { before: string; after?: string }> = {
  subject: { before: 'I need strategies for' },
  barrier: { before: 'to address a barrier related to' },
  grades: { before: 'for students in grades' },
  teachingType: { before: 'to', after: 'a skill/concept' },
  setting: { before: 'in a', after: 'instructional setting.' }
}

const MORE_OPTION_FIELDS = ['teachingType', 'setting']

const route = useRoute()
const router = useRouter()
const analytics = getAnalyticsTracker()
const { updateContext: updateFeedbackContext } = useUserFeedbackContext()
const { tags, loadingOptions, optionsError } = useStrategyTags()

const selection = computed(() => selectionFromQuery(route.query))
const subjectIsOther = computed(() => selection.value.subject === OTHER_SUBJECT)

const wizardFocus = ref(Boolean(selection.value.subject))
// a filter narrowing the results should never be hidden behind a collapsed disclosure
const showMoreFilters = ref(Boolean(selection.value.setting || selection.value.teachingType))

const promptRows = computed(() =>
  buildFilters(selection.value, tags.value)
    .filter(isAsked)
    .map((filter) => ({
      ...filter,
      emptyText: promptFor(filter),
      clearable: isClearable(filter),
      ...PROMPTS[filter.field]
    }))
)

const subjectRow = computed(() => promptRows.value.find((row) => row.field === 'subject'))
const barrierAndGradesRows = computed(() =>
  promptRows.value.filter((row) => row.field !== 'subject' && !MORE_OPTION_FIELDS.includes(row.field))
)
const moreOptionRows = computed(() => promptRows.value.filter((row) => MORE_OPTION_FIELDS.includes(row.field)))

const submitDisabled = computed(() => subjectIsOther.value && !selection.value.barrier)

function isAsked(filter: StrategyFilter) {
  if (filter.field === 'subject') {
    return true
  }
  if (!selection.value.subject) {
    return false
  }
  return filter.field !== 'barrier' || filter.options.length > 0
}

function promptFor(filter: StrategyFilter) {
  if (filter.field === 'subject') {
    return SUBJECT_PROMPT
  }
  if (filter.field === 'barrier' && subjectIsOther.value) {
    return BARRIER_PROMPT_FOR_OTHER
  }
  return filter.emptyText
}

function isClearable(filter: StrategyFilter) {
  if (filter.field === 'subject') {
    return false
  }
  return !(filter.field === 'barrier' && subjectIsOther.value)
}

function filtersEventData() {
  return {
    subject: selection.value.subject || undefined,
    barrier: selection.value.barrier || undefined,
    grades: selection.value.grades || undefined,
    setting: selection.value.setting || undefined,
    teachingType: selection.value.teachingType || undefined,
    query: selection.value.query || undefined
  }
}

function updateFilter(field: keyof StrategyFilterSelection, value: string | string[] | null) {
  const nextSelection = { ...selection.value, [field]: value } as StrategyFilterSelection
  if (field === 'subject') {
    nextSelection.barrier = ''
  }
  router.push({ name: 'StrategyWizard', query: selectionToQuery(nextSelection, route.query) })
}

function unfocus() {
  wizardFocus.value = false
  showMoreFilters.value = false
}

function startOver() {
  showMoreFilters.value = false
  // starting over clears the selection but must not clear the opt-in
  router.replace({ name: 'StrategyWizard', query: selectionToQuery(EMPTY_SELECTION, route.query) })
}

function findStrategies() {
  // the button stays focusable while disabled so its aria-describedby reason is reachable by Tab
  if (submitDisabled.value) {
    return
  }
  // route.query carries the ?beta=true opt-in across the handoff, so pages still in the
  // beta, such as a strategy opened from the results, stay on Vue 3
  router.push({ name: 'BrowseStrategiesPage', query: selectionToQuery(selection.value, route.query) })
  analytics.sendEvent(EVENT_NAMES.STRATEGY_WIZARD.SUBMITTED, filtersEventData())
}

async function toggleMoreFilters() {
  showMoreFilters.value = !showMoreFilters.value
  if (!showMoreFilters.value) {
    return
  }
  await nextTick()
  const firstRevealed = document.querySelector<HTMLElement>(`[data-testid="filter-${MORE_OPTION_FIELDS[0]}"]`)
  firstRevealed?.focus()
}

watch(
  () => Boolean(selection.value.setting || selection.value.teachingType),
  (hasMoreOption) => {
    if (hasMoreOption) {
      showMoreFilters.value = true
    }
  }
)

watch(
  () => selection.value.subject,
  (subject) => updateFeedbackContext({ subject }),
  { immediate: true }
)

analytics.sendEvent(EVENT_NAMES.STRATEGY_WIZARD.VIEWED_PAGE, {
  ...filtersEventData(),
  ref: route.query.ref
})
</script>

<template>
  <div class="container">
    <PageHeader title="Strategy Wizard" subtitle="Find specific strategies for a particular subject and area of need." />

    <div>
      <WizardOverlayContainer :activated="wizardFocus" theme="green" label="Strategy Wizard" @unfocus="unfocus">
        <WizardPrompt
          v-if="subjectRow"
          :filter="subjectRow"
          :before="subjectRow.before"
          :clearable="subjectRow.clearable"
          @open="wizardFocus = true"
          @select="updateFilter('subject', $event)"
        />

        <div v-if="selection.subject">
          <WizardPrompt
            v-for="row in barrierAndGradesRows"
            :key="row.field"
            :filter="row"
            :before="row.before"
            :clearable="row.clearable"
            @open="wizardFocus = true"
            @select="updateFilter(row.field, $event)"
          />

          <div v-if="showMoreFilters">
            <WizardPrompt
              v-for="row in moreOptionRows"
              :key="row.field"
              :filter="row"
              :before="row.before"
              :after="row.after"
              :clearable="row.clearable"
              @open="wizardFocus = true"
              @select="updateFilter(row.field, $event)"
            />
          </div>

          <button
            type="button"
            :aria-expanded="showMoreFilters"
            aria-live="polite"
            class="text-text-md text-blue-400 underline-offset-4 hover:underline focus-visible:outline-offset-2"
            @click="toggleMoreFilters"
          >
            {{ showMoreFilters ? 'Fewer options' : 'More options' }}
          </button>

          <div class="mt-6 text-center">
            <button
              type="button"
              :aria-disabled="submitDisabled"
              :aria-describedby="submitDisabled ? SUBMIT_DISABLED_REASON_ID : undefined"
              class="rounded-xs border border-transparent bg-blue-400 px-6 py-4 text-display-sm leading-8 text-white hover:bg-blue-500 aria-disabled:opacity-65 aria-disabled:hover:bg-blue-400 focus-visible:outline-offset-2"
              @click="findStrategies"
            >
              Find Strategies
            </button>
            <span v-if="submitDisabled" :id="SUBMIT_DISABLED_REASON_ID" class="sr-only">
              {{ SUBMIT_DISABLED_REASON }}
            </span>
            <button
              data-testid="start-over"
              type="button"
              class="ml-4 px-6 py-4 text-display-sm text-blue-400 underline-offset-4 hover:underline focus-visible:outline-offset-2"
              @click="startOver"
            >
              Start Over
            </button>
          </div>
        </div>

        <div v-if="loadingOptions" class="mt-6 flex items-center text-text-lg text-gray-800">
          <ProgressSpinner stroke-width="8" class="!ml-0 !mr-2 h-4 w-4 stroke-gray-700" aria-hidden="true" />
          <p role="status">Loading options...</p>
        </div>
        <p v-else-if="optionsError" role="alert" class="mt-6 text-text-lg text-gray-900">{{ optionsError }}</p>

        <div v-if="!wizardFocus" class="text-right">
          <router-link
            :to="{ name: 'StrategyIndex', query: betaOptIn(route.query) }"
            class="whitespace-nowrap text-blue-400 underline-offset-4 hover:underline focus:outline-offset-4"
          >
            View strategy index
          </router-link>
        </div>
      </WizardOverlayContainer>
    </div>

  </div>
</template>
